import { ContextMenuButton, Td, Tr } from 'app/components/Table';
import { MouseEvent } from 'react';
import { ReactComponent as LineIcon } from 'resources/images/line.svg';
import { IconButton, Text } from '@chakra-ui/react';
import { ReactComponent as NotesIcon } from 'resources/images/notes.svg';
import { Audio } from 'types/audio';
import { Row } from '@tanstack/react-table';
import { Song } from 'types/song';
import { ListItemText } from 'app/components/ChakraTableList';
import { useAppSelector } from 'app/store';
import { getIsSettingPrivateAudio } from 'app/store/SongList/selectors';
import { AudioPlayControls } from '../AudioPlayControls';
import { AudioTitle } from '../AudioTitle';
import {
  getBytestoMBText,
  getDateColumnText,
  getErrorText,
  getFormattedDuration,
} from './utils';
import { RetryControls } from '../RetryControls';
import { PrivateCheckbox } from '../PrivateCheckbox';
import { AudioDialogVariant } from './types';
import { useAudioPlayHook } from '../AudioPlayControls/AudioPlayHook';

interface AudioTableRowProps {
  row: Row<Audio>;
  isLoading: ({ isAudioLoading, audioPlayerGuid }) => boolean;
  song?: Song;
  isError?: boolean;
  handleRetry: (audio: Audio, songGuid?: string | undefined) => () => void;
  handleDelete: (audio: Audio) => () => Promise<void>;
  togglePrivateAudio: (audio: Audio) => Promise<void>;
  setDialogOpen: (props: AudioDialogVariant) => void;
  contextHandler: (e: MouseEvent<HTMLDivElement | HTMLButtonElement>) => void;
}

export const AudioTableRow = ({
  row,
  song,
  isLoading = () => false,
  isError,
  handleRetry,
  handleDelete,
  togglePrivateAudio,
  setDialogOpen,
  contextHandler,
}: AudioTableRowProps) => {
  const isSettingPrivate = useAppSelector(getIsSettingPrivateAudio);

  const { getToggleExpandedHandler, parentId } = row;

  const audio = row.original;
  const audioGuid = audio.guid;
  const parentGuid = song?.guid || audio.parent;
  const songDetails = { status: song?.status, title: song?.title };

  const { handlePlay } = useAudioPlayHook({
    audioGuid,
    parentGuid,
    songDetails,
  });

  if (parentId === undefined) {
    return (
      <Tr
        zIndex={1}
        position="relative"
        role="row"
        data-group
        sx={{
          _hover: {},
          bg: 'transparent !important',
        }}
      >
        <Td
          sx={{
            fontWeight: 500,
            overflow: 'visible',
            color: 'textSubdued',
          }}
        >
          {row.original.type}
        </Td>
        <Td />
        <Td />
        <Td />
        <Td />
        <Td />
        <Td />
        <Td />
        <Td />
        <Td
          sx={{
            position: 'absolute',
            right: 0,
            lineHeight: '48px',
            fontSize: 'xs',
            fontWeight: 500,
            color: 'textSubdued',
            cursor: 'pointer',
            _groupHover: { color: 'textBase' },
          }}
          onClick={getToggleExpandedHandler()}
        >
          {row.getIsExpanded() ? 'Hide section' : 'Show section'}
        </Td>
      </Tr>
    );
  }

  const audioFileSize = getBytestoMBText(audio.fileSize);
  const audioFileDuration = getFormattedDuration(audio.duration);

  return (
    <Tr
      zIndex={1}
      position="relative"
      role="row"
      data-group
      even
      className={row.index % 2 === 0 ? 'even' : 'odd'}
      sx={{ _even: {} }}
      onContextMenu={contextHandler}
      onDoubleClick={handlePlay}
    >
      <Td>
        <AudioPlayControls
          songDetails={{ status: song?.status, title: song?.title }}
          isLoading={isLoading}
          audioGuid={audioGuid}
          parentGuid={parentGuid}
          isError={isError}
        />
      </Td>
      <Td sx={{ pl: 0 }}>
        <AudioTitle
          audioGuid={audio.guid}
          title={audio.title}
          isLatestVersion={audio.isLatestVersion}
        />
      </Td>
      <Td>
        {isError ? (
          <>
            <Text data-testid="audio-upload-error-message">
              {getErrorText(audio)}
            </Text>
            <RetryControls
              handleRetry={audio.file && handleRetry(audio)}
              handleDelete={handleDelete(audio)}
            />
          </>
        ) : (
          <ListItemText
            data-testid="audio-date"
            primary={getDateColumnText(audio)}
          />
        )}
      </Td>
      <Td>
        <ListItemText
          primary={audio.createdBy.profileCard.displayName}
          title={audio.createdBy.profileCard.displayName}
          data-testid="audio-user-display-name"
        />
      </Td>
      <Td>
        <ListItemText
          primary={audioFileSize || <LineIcon />}
          data-testid="audio-file-size"
        />
      </Td>
      <Td>
        <ListItemText
          primary={audioFileDuration || <LineIcon />}
          data-testid="audio-file-duration"
        />
      </Td>
      <Td>
        {audio.remark ? (
          <ListItemText
            title={audio.remark}
            primary={audio.remark}
            data-testid="audio-note-button"
            primaryTextProps={{
              cursor: 'pointer',
              maxWidth: '255px',
              onClick: () => {
                setDialogOpen({ type: 'note', audio });
              },
            }}
          />
        ) : (
          <IconButton
            aria-label="audio note"
            variant="text"
            color="baseText"
            size="sm"
            minW="unset"
            data-testid="audio-note-button"
            onClick={() => setDialogOpen({ type: 'note', audio })}
          >
            {audio.remark ? <NotesIcon /> : <LineIcon />}
          </IconButton>
        )}
      </Td>
      <Td>
        <PrivateCheckbox
          isUpdating={Boolean(isSettingPrivate[audio.guid])}
          audio={audio}
          handleCheckboxAudio={() => togglePrivateAudio(audio)}
        />
      </Td>
      <Td />
      <Td>
        <ContextMenuButton onClick={contextHandler} />
      </Td>
    </Tr>
  );
};
